import React, {
  Suspense,
  useContext,
  lazy,
  useMemo,
  useEffect,
  useState,
} from 'react'
import objectPath from 'object-path'
import Loader from 'components/Loader/Loader'
import routes from '../../../routes'
import { DesignContext } from 'contexts/DesignContext'
import {
  getAllFormGroup,
  getCurrentUi,
  setRequestFormData,
  updateFormDataValue,
} from '../action/Data'
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom'
import { callAction } from '../action/Action'
import { onCheckCondition } from '../action/condition/Action'
import { hasRules, onCheckRules } from '../rules/Action'
import { debounceCallback } from 'utility/debounceCallback.util'
import { updateModalReceiveFilesFormData } from './modalReceiveData.util'

const getTriggerButtonScopeKey = (designContext = {}) => {
  let { formDesign = {}, jsonForm = {} } = designContext

  return (
    formDesign.code ||
    jsonForm.formCode ||
    formDesign.formDesignId ||
    jsonForm.formDesignId ||
    ''
  )
}

const createTriggerButtonResponse = (jsonForm = {}) => ({
  status: 200,
  message: '',
  data: objectPath.get(jsonForm, 'formData') || {},
})

const isRoutePathMatch = (routePath, componentPath) => {
  if (Array.isArray(routePath)) return routePath.includes(componentPath)

  return routePath === componentPath
}

const CustomComponent = (props) => {
  let {
    uischema,
    action,
    config,
    closeExternalModal,
    modalDynamicIndex,
    modalOpenSource,
    isModalCondition,
  } = props
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    dynamicKey,
  } = config || {}
  let uischemaKey = uischema.key
  let designerKey = uischema.name
  let uiType = uischema.uiType
  const componentPath = action?.component || ''
  const components = routes.find((route) => route.routes && route.path === '*')
  const patientFileComponent = routes.find(
    (route) => route.routes && route.path !== '*',
  )
  const targetComponentPath = uischema?.path || componentPath

  const foundComponent =
    components?.routes?.find(
      (route) => isRoutePathMatch(route.path, targetComponentPath),
    ) ||
    patientFileComponent?.routes?.find(
      (route) => isRoutePathMatch(route.path, targetComponentPath),
    ) ||
    {}
  const sourceType = action?.sourceType || ''
  const Component = foundComponent?.component
    ? foundComponent.component
    : isModalCondition
    ? lazy(() => import(`designer/display/modal`))
    : sourceType === 'page'
    ? lazy(() => import(`designer/display/page`))
    : lazy(() => import(`designer/display/form`))

  const defaultProps = Component?._payload?._result?.defaultProps
  const componentProps = defaultProps?.designerData || {}
  const designContext = useContext(DesignContext)
  const {
    jsonForm,
    setFormData,
    setDesignerPropsData,
    setOpenModal,
    openModal,
    findUischemaIndex,
    setUischema,
    searchData,
    setFieldSearchData,
    isResetData,
    elementAction,
    callback = {},
    setCallback,
    designerEvent = {},
    setDesignerEvent,
    isUiChanged,
    setIsUiChanged,
    isPage,
    tabParamActive,
  } = designContext
  let isEnable = objectPath.has(elementAction, ['isEnable', uischema.key])
    ? objectPath.get(elementAction, ['isEnable', uischema.key])
    : true
  let designerPropsData = jsonForm?.designerPropsData || {}
  let formData = objectPath.get(jsonForm, 'formData') || {}
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()
  const search = location.search || []
  const receivePathParams = match.params
  const urlSearchParams = new URLSearchParams(search)
  const receiveQueryParams = Object.fromEntries([...urlSearchParams])
  const allParams = { ...receivePathParams, ...receiveQueryParams }
  const datasourceValue = objectPath.get(designContext, `@datasource`) || {}

  const uiKey = useMemo(() => {
    if (isDynamic) {
      return `${dynamicName}.${dynamicIndex}`
    }
    if (openModal) {
      return openModal
    }
    return designerKey
  }, [uischema.key])

  const tabList = useMemo(() => {
    let mapKey = jsonForm.schema.mapKey || {}
    let schemaKey = mapKey[uischema.key]
    if (!schemaKey) return []
    let schemaKeyList = schemaKey.split('.') || []
    let tabList = []
    schemaKeyList.forEach((key) => {
      let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      if (ui.key === key && ui.tagname === 'tab') {
        tabList.push(key)
      }
    })
    return tabList
  }, [uischema.key])

  const designerData = objectPath.get(designerPropsData, `data`) || {}
  const designerProps = objectPath.get(designerPropsData, `props`) || []
  const favoriteProps = objectPath.get(designerPropsData, `favoriteProps`) || []
  const allDesignerProps = [...favoriteProps]
  const receiveProps = objectPath.get(designerPropsData, `receive`) || []
  const [isRenderComponent, setIsRenderComponent] = useState(true)

  const closeInternalModal = (modalKey) => {
    let elements = jsonForm.uischema.elements
    let modalElement = getModal(jsonForm, modalKey)
    modalElement.isOpen = false
    let newUiSchema = jsonForm.uischema
    newUiSchema.elements = elements
    setUischema(newUiSchema)
    setOpenModal(null)
  }

  const getModal = (form, modalKey) => {
    let elements = form.uischema.elements
    let mapKey = form.schema.mapKey[modalKey]
    let indices = findUischemaIndex(modalKey, mapKey, elements)
    let modalElement = indices.reduce((o, i) => o.elements[i], { elements })
    return modalElement
  }

  let currentSearch = useMemo(() => {
    let fieldSearch = objectPath.get(jsonForm, 'search.field') || []
    let found = fieldSearch.find(({ key }) => key === uischema.key)
    return found ? true : false
  }, [uischema.key])

  const onCloseModal = (
    componentPropsData = {},
    receiveProps = allDesignerProps,
  ) => {
    let designerData = objectPath.get(jsonForm, 'designerPropsData.data') || {}
    let componentData = objectPath.get(componentPropsData, 'data') || {}
    let data = { ...designerData, ...componentData }
    if (closeExternalModal) {
      closeExternalModal(componentPropsData, receiveProps)
    }
    setOpenModal(null)
  }

  const onSelectRow = (rowData, isClosedModal, receiveProps) => {
    let data = objectPath.get(designerPropsData, 'data') || {}
    data = { ...data, ...rowData }
    objectPath.set(designerPropsData, 'data', data)
    let destinationData = formData
    let destinationSearch = searchData
    allDesignerProps.forEach((prop) => {
      let propName = prop.prop
      let system = prop?.system || []
      let systemType = prop?.systemType
      let rowValue = objectPath.get(data, propName) || {}
      system.forEach(({ field, systemKey, isSearch }) => {
        let value = objectPath.get(rowValue, field)
        if (systemType === 'input') {
          value = objectPath.get(data, field)
        }
        if (isSearch) {
          let ui = getCurrentUi(systemKey, jsonForm.schema, jsonForm.uischema)
          if (ui.key === systemKey) {
            setFieldSearchData(ui, systemKey, value)
          } else {
            setFieldSearchData(uischema, systemKey, value)
          }
        }
        objectPath.set(destinationData, systemKey, value)
      })
      setFormData(destinationData)
    })
    setDesignerPropsData(designerPropsData)
    if (isClosedModal) {
      if (action) {
        let inputAppendPropsData =
          modalOpenSource === 'inputAppend'
            ? {
                data,
                isClosed: true,
                isSelectedRow: true,
              }
            : {}
        onCloseModal(
          inputAppendPropsData,
          receiveProps.filter(({ propReceive }) => !propReceive),
        )
      } else {
        closeInternalModal(openModal)
      }
    }
  }

  const [render, setRender] = useState(false)
  const [dataUpdate, setDataUpdate] = useState({})

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  const onUpdateData = (data) => {
    setRender(true)
    setDataUpdate(data)
    Object.keys(componentProps).forEach((componentKey) => {
      let value = objectPath.get(data, componentKey)
      let key = isDynamic
        ? `${uiKey}.jsonData.${componentKey}`
        : `${designerKey}.${componentKey}`
      objectPath.set(formData, key, value)
    })
    setFormData(formData)
  }

  const onUpdateFormData = (data = {}) => {
    const nextFormData = { ...formData }
    const mapName = objectPath.get(jsonForm, 'schema.mapName') || {}

    Object.entries(data).forEach(([field, value]) => {
      objectPath.set(nextFormData, field, value)

      Object.entries(mapName).forEach(([key, name]) => {
        const fieldName = String(name || '').split('.').pop()
        if (key !== field && name !== field && fieldName !== field) return

        objectPath.set(nextFormData, key, value)
        objectPath.set(nextFormData, name, value)
        objectPath.set(nextFormData, ['@mapResponse', key], true)
      })
    })

    setFormData(nextFormData)
    setIsUiChanged({
      ...isUiChanged,
      mapResponse: new Date().getTime(),
    })
  }

  const onUpdateDesignerData = (designerData, propName, value) => {
    let componentProps = jsonForm?.designerPropsData?.props || []
    let formGroup = getAllFormGroup(jsonForm, designContext)
    if (propName) {
      if (objectPath.has(designerData, designerKey)) {
        objectPath.set(designerData, `${designerKey}.${propName}`, value)
      } else {
        objectPath.set(designerData, propName, value)
      }
      let findComponentProp = componentProps.find(
        ({ fullPropKey }) => fullPropKey === `${designerKey}.${propName}`,
      )
      if (findComponentProp) {
        let { systemType, systemKey, systemName, fullPropKey } =
          findComponentProp
        if (systemType === 'input') {
          let ui = getCurrentUi(systemKey, jsonForm.schema, jsonForm.uischema)
          objectPath.set(formData, systemKey, value)
          if (ui.key === systemKey) {
            objectPath.set(formData, ui.name, value)
          }
        } else if (systemType === 'datasource') {
          objectPath.set(formData, systemKey, value)
        } else if (systemType === 'formData') {
          let source = objectPath.get(designerData, fullPropKey)
          formData = updateFormDataValue({
            jsonForm,
            formGroup: formGroup,
            formDataName: systemName,
            formDataKey: systemKey,
            source: source,
            destination: formData,
            type: 'prop',
            isRequired: false,
          })
        } else if (systemType === 'files') {
          let result = updateModalReceiveFilesFormData({
            formData,
            data: designerData,
            prop: findComponentProp.prop,
            propKey: findComponentProp.propKey,
            fullPropKey,
            systemKey,
          })
          formData = result.formData
        }
      }
    } else {
      let filterProps = componentProps.filter(
        ({ elementKey }) => elementKey === uischema.key,
      )
      filterProps.forEach(
        ({ fullPropKey, systemType, systemKey, systemName, prop, propKey }) => {
          if (systemType === 'input') {
            let ui = getCurrentUi(systemKey, jsonForm.schema, jsonForm.uischema)
            let value = objectPath.get(designerData, fullPropKey)
            objectPath.set(formData, systemKey, value)
            if (ui.key === systemKey) {
              objectPath.set(formData, ui.name, value)
            }
          } else if (systemType === 'system') {
            let value = objectPath.get(designerData, fullPropKey)
            objectPath.set(formData, systemKey, value)
          } else if (systemType === 'formData') {
            let source = objectPath.get(designerData, fullPropKey)
            formData = updateFormDataValue({
              jsonForm,
              formGroup: formGroup,
              formDataName: systemName,
              formDataKey: systemKey,
              source: source,
              destination: formData,
              type: 'prop',
              isRequired: false,
            })
          } else if (systemType === 'files') {
            let result = updateModalReceiveFilesFormData({
              formData,
              data: designerData,
              prop,
              propKey,
              fullPropKey,
              systemKey,
            })
            formData = result.formData
          }
        },
      )
    }

    objectPath.del(formData, `${designerKey}.undefined`)
    objectPath.del(formData, 'undefined')
    setFormData(formData)
    objectPath.set(isUiChanged, uischema.key, { isRender: true })
    setIsUiChanged(isUiChanged)
    const hasDesignerRules = hasRules(jsonForm)
    const conditions = jsonForm?.conditions || []
    if (hasDesignerRules) {
      onCheckRules(designContext, formData)
    }
    if (!hasDesignerRules || conditions.length > 0) {
      onCheckCondition(designContext, formData)
    }
    objectPath.set(designerPropsData, 'data', designerData)
    setDesignerPropsData(designerPropsData)
  }

  const onUpdateProps = (props = {}) => {
    let system = []

    let propUpdate = {
      prop: designerKey,
      propType: 'formData',
      propKey: uischema.key,
      propSend: true,
      propReceive: true,
      systemType: 'formData',
      systemName: designerKey,
      systemKey: uischema.key,
      system: system,
      formDataName: designerKey,
      key: uischema.key,
    }

    if (isDynamic) {
      objectPath.set(propUpdate, 'systemName', dynamicName)
      objectPath.set(propUpdate, 'systemKey', dynamicKey)
      objectPath.set(propUpdate, 'system', [
        {
          field: dynamicName,
          systemName: dynamicName,
          systemKey: dynamicKey,
        },
      ])
    } else {
      Object.keys(props).forEach((propName) => {
        system.push({
          field: propName,
          systemName: propName,
          systemKey: propName,
        })
      })
      objectPath.set(propUpdate, 'system', system)
    }

    let foundIndex = receiveProps.findIndex(
      (item) => uischema.key === item?.propKey,
    )
    if (foundIndex > -1) {
      objectPath.set(receiveProps, foundIndex, propUpdate)
    } else {
      receiveProps.push(propUpdate)
    }
    objectPath.set(jsonForm, 'designerPropsData.receive', receiveProps)
    let componentProps = jsonForm?.designerPropsData?.props || []
    let dataUpdate = designerData
    let formData = jsonForm.formData
    componentProps.forEach(
      ({ fullPropKey, systemType, systemValue, customType, system = [] }) => {
        if (systemType === 'input') {
          system.forEach(({ dynamicName }) => {
            let value = objectPath.get(formData, dynamicName)
            objectPath.set(dataUpdate, fullPropKey, value)
          })
        } else if (systemType === 'custom') {
          if (customType === 'number') {
            systemValue = Number(systemValue)
          } else if (customType === 'array' || customType === 'object') {
            if (typeof systemValue === 'string') {
              systemValue = JSON.parse(systemValue)
            }
          }
          objectPath.set(dataUpdate, fullPropKey, systemValue)
        }
      },
    )
    setDataUpdate(dataUpdate)
  }

  const onCreateCallback = (designerKey, callFunction) => {
    objectPath.set(callback, designerKey, callFunction)
    setCallback(callback)
  }

  const onCallDesignerEvent = async (designerKey, callEvent) => {
    let componentProps = jsonForm?.designerPropsData?.props || []
    let foundEvent = componentProps.find(({ systemType, fullPropKey }) => {
      return (
        systemType === 'event' &&
        (fullPropKey === `${designerKey}.${callEvent}` ||
          fullPropKey === callEvent)
      )
    })
    if (foundEvent) {
      let actionName = foundEvent.systemKey
      let action = foundEvent.action
      let data = {
        designerKey,
        allParams,
        designerData,
      }
      let routerDom = { history, match, location }
      let result = await callAction(
        actionName,
        action,
        designContext,
        data,
        routerDom,
      )
    }
  }

  const createTriggerButtonCallback = (buttonKey) => {
    return async () => {
      let triggerScopeKey = getTriggerButtonScopeKey(designContext)

      return new Promise((resolve, reject) => {
        let timeout = setTimeout(() => {
          resolve(createTriggerButtonResponse(jsonForm))
        }, 120000)

        let triggerPayload = {
          isClickButton: true,
          resolve: (response) => {
            clearTimeout(timeout)
            resolve(response || createTriggerButtonResponse(jsonForm))
          },
          reject: (error) => {
            clearTimeout(timeout)
            reject(error)
          },
        }

        if (triggerScopeKey) {
          objectPath.set(
            isUiChanged,
            ['triggerButton', triggerScopeKey, buttonKey],
            triggerPayload,
          )
        } else {
          objectPath.set(isUiChanged, buttonKey, triggerPayload)
        }
        setIsUiChanged({ ...isUiChanged })
      })
    }
  }

  const updateComponentProp = (formData, designerData = {}) => {
    let componentProps = jsonForm?.designerPropsData?.props || []
    let dataUpdate = designerData
    componentProps.forEach((itemProp) => {
      let {
        prop,
        fullPropKey,
        systemType,
        systemValue,
        systemKey,
        propReceive,
        prefixValue = '',
        customType,
      } = itemProp
      if (!propReceive) return
      let value
      if (systemType === 'input') {
        value = objectPath.get(formData, systemKey)
      } else if (systemType === 'custom') {
        if (customType === 'number') {
          systemValue = Number(systemValue)
        } else if (customType === 'array' || customType === 'object') {
          if (typeof systemValue === 'string') {
            systemValue = JSON.parse(systemValue)
          }
        }
        value = systemValue
      } else if (systemType === 'formData') {
        let formGroup = getAllFormGroup(jsonForm, designContext)
        let form = formGroup.find(({ key }) => key === systemKey)
        let dataSystem = {}
        value = setRequestFormData(form, dataSystem, {
          datasourceValue,
          formData,
          allParams,
          jsonForm,
        })
      } else if (systemType === 'apiResponse') {
        value = objectPath.get(formData, `@response.${systemKey}`)
      } else if (systemType === 'tab') {
        if (objectPath.has(tabParamActive, systemKey)) {
          let tabParam = objectPath.get(tabParamActive, systemKey)
          let foundTab = tabParam.find(
            (item) => `${prop}.${item.name}` === fullPropKey,
          )
          value = foundTab?.value
        }
      } else if (systemType === 'activeTab') {
        value = objectPath.get(isUiChanged, ['activeTab', systemKey])
      } else if (systemType === 'triggerButton' && systemKey) {
        value = createTriggerButtonCallback(systemKey)
      }
      if (
        value &&
        prefixValue &&
        !['formData', 'triggerButton'].includes(systemType)
      ) {
        value = prefixValue + value
      }
      objectPath.set(dataUpdate, fullPropKey || prop, value)
    })
    setDataUpdate(dataUpdate)
  }

  const isFetchComponent = objectPath.get(
    isUiChanged,
    `${uischema.key}.isFetchComponent`,
  )

  useEffect(() => {
    if (isFetchComponent) {
      setIsRenderComponent(false)
      debounceCallback(
        setIsRenderComponent,
        [true],
        'setIsRenderComponent',
        100,
      )
      objectPath.set(isUiChanged, uischema.key, {
        isRender: false,
        isFetchComponent: false,
      })
      setIsUiChanged(isUiChanged)
    }
  }, [isFetchComponent])

  useEffect(() => {
    if (componentProps) {
      // onUpdateProps(componentProps)
      setIsRenderComponent(true)
    }
  }, [uischema.key])

  useEffect(() => {
    if (isResetData) {
      Object.keys(componentProps).forEach((key) => {
        objectPath.set(designerData, `${uiKey}.${key}`, '')
      })
    }
  }, [isResetData])

  useEffect(() => {
    if (isPage === 1) {
      updateComponentProp({ ...formData, ...searchData }, designerData)
    }
  }, [JSON.stringify(searchData)])

  useEffect(() => {
    if (isPage !== 1) {
      updateComponentProp(formData, designerData)
    }
  }, [JSON.stringify(formData)])

  return (
    <Suspense fallback={isPage === 1 ? null : <Loader />}>
      <div
        style={{
          padding: uischema.layoutPadding,
          margin: uischema.layoutMargin,
        }}
      >
        {isRenderComponent && (
          <Component
            {...props}
            uischemaKey={uischemaKey}
            designerKey={designerKey}
            designerConfig={config}
            designerProps={allDesignerProps}
            designerData={dataUpdate}
            modalDynamicIndex={modalDynamicIndex}
            onUpdateProps={onUpdateProps}
            onUpdateData={onUpdateData}
            onUpdateFormData={onUpdateFormData}
            onUpdateDesignerData={onUpdateDesignerData}
            onSelectRow={onSelectRow}
            onCloseModal={onCloseModal}
            onCreateCallback={onCreateCallback}
            onCallDesignerEvent={onCallDesignerEvent}
            isEnable={isEnable}
            isUiChanged={isUiChanged}
            setIsUiChanged={setIsUiChanged}
            tabs={tabList}
          />
        )}
      </div>
    </Suspense>
  )
}

export default CustomComponent
